import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, getDatabase, ref, update } from "firebase/database";
import "./balance_update.css";

const formatBalance = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return String(value ?? "");
  }

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const getBalanceValue = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const BalanceUpdatePage = ({ onClose }) => {
  const [rows, setRows] = useState([]);
  const [editedBalances, setEditedBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch balances and company names from Firebase.
  const fetchBalances = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const db = getDatabase();

      const [codesSnapshot, dashboardProfilesSnapshot] =
        await Promise.all([
          get(ref(db, "new-code")),
          get(ref(db, "profileDashboard")),
        ]);

      const data = codesSnapshot.exists()
        ? codesSnapshot.val()
        : {};

      const dashboardProfiles = dashboardProfilesSnapshot.exists()
        ? dashboardProfilesSnapshot.val()
        : {};

      const result = [];

      Object.entries(data).forEach(([userId, userData]) => {
        const codes = userData?.codes;

        if (!codes || typeof codes !== "object") {
          return;
        }

        Object.entries(codes).forEach(([codeKey, codeData]) => {
          if (!codeData || typeof codeData !== "object") {
            return;
          }

          result.push({
            rowKey: `${userId}::${codeKey}`,
            userId,
            codeKey,

            companyName:
              dashboardProfiles[userId]?.company_name ||
              codeData.companyName ||
              codeData.company ||
              "—",

            codeId: codeData.codeID || "—",
            brand: codeData.brand || "—",
            balance: Number(codeData.balance) || 0,
          });
        });
      });

      // Sort by company name, then Code ID.
      result.sort((a, b) => {
        const companyComparison = String(a.companyName).localeCompare(
          String(b.companyName)
        );

        if (companyComparison !== 0) {
          return companyComparison;
        }

        return String(a.codeId).localeCompare(String(b.codeId));
      });

      // Code numbers run continuously across the whole table.
      setRows(
        result.map((row, index) => ({
          ...row,
          rowNumber: index + 1,
          originalBalance: row.balance,
        }))
      );

      setEditedBalances({});
    } catch (err) {
      console.error("Failed to fetch balances:", err);
      setError(err.message || "Unable to fetch balances.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  // Determine which rows have changed.
  const changedRows = useMemo(() => {
    return rows
      .filter((row) =>
        Object.prototype.hasOwnProperty.call(
          editedBalances,
          row.rowKey
        )
      )
      .map((row) => ({
        ...row,
        newBalance: editedBalances[row.rowKey],
      }));
  }, [rows, editedBalances]);

  // Group consecutive code rows by company.
  const groupedRows = useMemo(() => {
    const groups = [];

    rows.forEach((row) => {
      const companyKey = String(row.companyName || "—")
        .trim()
        .toLocaleLowerCase();

      let group = groups[groups.length - 1];

      if (!group || group.companyKey !== companyKey) {
        group = {
          companyKey,
          companyName: row.companyName || "—",

          // Number each company group separately.
          companyNumber: groups.length + 1,

          rows: [],
          shadeIndex: groups.length % 2,
        };

        groups.push(group);
      }

      group.rows.push(row);
    });

    return groups;
  }, [rows]);

  // Handle edits to the balance column only.
  const handleBalanceChange = (row, value) => {
    setEditedBalances((previous) => {
      const next = { ...previous };

      const originalValue = getBalanceValue(row.originalBalance);
      const newValue = getBalanceValue(value);

      if (
        value !== "" &&
        newValue !== null &&
        originalValue === newValue
      ) {
        delete next[row.rowKey];
      } else {
        next[row.rowKey] = value;
      }

      return next;
    });
  };

  // Validate edits before showing the confirmation dialog.
  const handleSaveClick = () => {
    setError("");

    if (changedRows.length === 0) {
      setError("There are no balance changes to save.");
      return;
    }

    const invalidRows = changedRows.filter((row) => {
      const value = row.newBalance;

      return (
        value === "" ||
        value === null ||
        value === undefined ||
        !Number.isFinite(Number(value)) ||
        Number(value) < 0
      );
    });

    if (invalidRows.length > 0) {
      setError(
        "Please enter a valid, non-negative balance for every changed row."
      );
      return;
    }

    setShowConfirmation(true);
  };

  // Save only the changed balance fields to Firebase.
  const handleConfirmSave = async () => {
    setSaving(true);
    setError("");

    try {
      const updates = {};

      changedRows.forEach((row) => {
        updates[
          `new-code/${row.userId}/codes/${row.codeKey}/balance`
        ] = Number(row.newBalance);
      });

      await update(ref(getDatabase()), updates);

      setRows((previousRows) =>
        previousRows.map((row) => {
          const change = changedRows.find(
            (item) => item.rowKey === row.rowKey
          );

          if (!change) {
            return row;
          }

          const savedBalance = Number(change.newBalance);

          return {
            ...row,
            balance: savedBalance,
            originalBalance: savedBalance,
          };
        })
      );

      setEditedBalances({});
      setShowConfirmation(false);
    } catch (err) {
      console.error("Failed to save balances:", err);

      setError(
        err.message ||
          "Some balance changes could not be saved. Please check Firebase."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="balance-update-page">
      <div className="balance-update-header">
        <div>
          <h2>Balance Update</h2>
          <p>
            View and update current company balances.
            Only the Balance column can be edited.
          </p>
        </div>

        <button
          type="button"
          className="balance-close-button"
          onClick={onClose}
          disabled={saving}
        >
          Close
        </button>
      </div>

      <div className="balance-toolbar">
        <div className="balance-summary">
          <span>
            Total records: <strong>{rows.length}</strong>
          </span>

          <span>
            Changed rows: <strong>{changedRows.length}</strong>
          </span>
        </div>

        <div className="balance-toolbar-actions">
          <button
            type="button"
            className="balance-refresh-button"
            onClick={fetchBalances}
            disabled={loading || saving}
          >
            Refresh
          </button>

          <button
            type="button"
            className="balance-save-button"
            onClick={handleSaveClick}
            disabled={
              loading || saving || changedRows.length === 0
            }
          >
            Save Changes ({changedRows.length})
          </button>
        </div>
      </div>

      {error && (
        <div className="balance-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="balance-loading">
          Fetching current balances...
        </div>
      ) : (
        <div className="balance-table-wrapper">
          <table className="balance-table">
            <thead>
              <tr>
                <th>Company No.</th>
                <th>Company Name</th>
                <th>Code No.</th>
                <th>Code ID</th>
                <th>Brand</th>
                <th>Current Balance</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="balance-empty">
                    No balance records found.
                  </td>
                </tr>
              ) : (
                groupedRows.map((group) =>
                  group.rows.map((row, indexInGroup) => {
                    const isChanged =
                      Object.prototype.hasOwnProperty.call(
                        editedBalances,
                        row.rowKey
                      );

                    const currentValue = isChanged
                      ? editedBalances[row.rowKey]
                      : row.originalBalance;

                    const groupShade =
                      group.shadeIndex === 0
                        ? "#ffffff"
                        : "#f3f7fc";

                    return (
                      <tr
                        key={row.rowKey}
                        className={
                          isChanged ? "balance-row-changed" : ""
                        }
                        style={{ backgroundColor: groupShade }}
                      >
                        {/* Company number appears once per company. */}
                        {indexInGroup === 0 && (
                          <td
                            rowSpan={group.rows.length}
                            style={{
                              backgroundColor: groupShade,
                              fontWeight: 600,
                              verticalAlign: "middle",
                              textAlign: "center",
                              borderRight: "1px solid #d8e1ee",
                            }}
                          >
                            {group.companyNumber}
                          </td>
                        )}

                        {/* Company name appears once per company. */}
                        {indexInGroup === 0 && (
                          <td
                            rowSpan={group.rows.length}
                            style={{
                              backgroundColor: groupShade,
                              fontWeight: 600,
                              verticalAlign: "middle",
                              borderRight: "2px solid #d8e1ee",
                            }}
                          >
                            {group.companyName}
                          </td>
                        )}

                        {/* Code number is separate and continuous. */}
                        <td style={{ textAlign: "center" }}>
                          {row.rowNumber}
                        </td>

                        <td>{row.codeId || "—"}</td>

                        <td>{row.brand || "—"}</td>

                        <td className="balance-edit-cell">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={currentValue ?? ""}
                            onChange={(event) =>
                              handleBalanceChange(
                                row,
                                event.target.value
                              )
                            }
                            aria-label={`Balance for ${
                              row.companyName || row.codeId
                            }`}
                            disabled={saving}
                          />

                          {isChanged && (
                            <span className="balance-changed-label">
                              Changed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation dialog showing old and new balances. */}
      {showConfirmation && (
        <div className="balance-modal-backdrop">
          <div
            className="balance-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="balance-confirm-title"
          >
            <h3 id="balance-confirm-title">
              Confirm Balance Changes
            </h3>

            <p>
              The following {changedRows.length} row(s) will be
              updated. Review the old and new balances before
              confirming.
            </p>

            <div className="balance-preview-wrapper">
              <table className="balance-table balance-preview-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Company</th>
                    <th>Code ID</th>
                    <th>Brand</th>
                    <th>Current Balance</th>
                    <th>New Balance</th>
                  </tr>
                </thead>

                <tbody>
                  {changedRows.map((row, index) => (
                    <tr key={row.rowKey}>
                      <td>{index + 1}</td>
                      <td>{row.companyName || "—"}</td>
                      <td>{row.codeId || "—"}</td>
                      <td>{row.brand || "—"}</td>
                      <td>
                        {formatBalance(row.originalBalance)}
                      </td>
                      <td className="balance-new-value">
                        {formatBalance(row.newBalance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="balance-confirm-actions">
              <button
                type="button"
                className="balance-cancel-button"
                onClick={() => setShowConfirmation(false)}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="balance-save-button"
                onClick={handleConfirmSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceUpdatePage;